import { Link } from 'react-router-dom';
import { DollarSign, MapPin, Search, ArrowRight, Star, Gift, Users, Sparkles, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const benefits = [
  {
    icon: DollarSign,
    pre: 'Cero comisiones',
    title: 'Tu producto, tus reglas, tu dinero.',
    text: 'Olvidate de que la plataforma se quede con el 20% de tu venta. En Bisne no somos intermediarios financieros; tu pones el precio, tu acuerdas el pago y tu te quedas con cada centavo.',
  },
  {
    icon: MapPin,
    pre: 'Trato local',
    title: 'Cierra tratos hoy mismo en tu zona.',
    text: 'Filtra por ciudad y encuentra a tu comprador a unas cuadras. Chatea en tiempo real, negocia y acuerden un punto de entrega.',
  },
  {
    icon: Search,
    pre: 'Mas que un bazar',
    title: 'No vendes nada? Ofrece tu talento o pide.',
    text: 'Vende tu bici vieja, anuncia el servicio a domicilio de tu negocio o publica un "Se Busca" con tu presupuesto maximo.',
  },
];

const testimonials = [
  { text: 'Subi el Xbox que ya no usaba y en la tarde ya lo habia cambiado por un cel mas una lana a mi favor. Super rapido el chat.', author: 'Carlos M.', loc: 'Toluca' },
  { text: 'Anuncie el servicio a domicilio de mi negocio y me cayeron pedidos de la misma colonia que ni sabia que existiamos.', author: 'Negocio Local', loc: 'Ocoyoacac' },
  { text: 'Puse un "Se Busca" para una refaccion de moto y me ahorre estar buscando en grupos. Aqui me mandaron la oferta directo.', author: 'Luis A.', loc: 'Lerma' },
];

const faqs = [
  { q: 'Cuanto cuesta publicar un anuncio?', a: 'Absolutamente nada. Publicar es 100% gratis y sin limites.' },
  { q: 'Como me pagan mis ventas?', a: 'El trato es directo. Tu decides si te pagan en efectivo o transferencia contra entrega. No retenemos dinero.' },
  { q: 'Que es Bisne Cash?', a: 'Es la moneda interna de Bisne. La ganas al abrir tu Boveda de Bienvenida y al invitar amigos. Podras usarla en futuras promociones y descuentos dentro de la app.' },
  { q: 'Es seguro usar Bisne?', a: 'Si. Contamos con verificacion de identidad (INE). Recomendamos hacer tratos con perfiles verificados y en lugares publicos.' },
  { q: 'Bisne se encarga de los envios?', a: 'No. La logistica de entrega corre 100% por cuenta tuya para mantener la app sin comisiones.' },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-zinc-950 text-foreground overflow-x-hidden">

      {/* ── Navbar ── */}
      <nav data-testid="landing-nav" className="sticky top-0 z-50 bg-zinc-950/90 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-5 py-3">
          <Link to="/welcome" className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Bisne" className="h-9 w-9 object-contain" />
            <span className="font-heading text-xl text-primary">Bisne</span>
          </Link>
          <Link to="/login">
            <Button data-testid="landing-login-btn" variant="outline" className="rounded-full font-body font-semibold text-sm border-zinc-700 text-zinc-300 hover:border-primary hover:text-primary px-5">
              Entrar
            </Button>
          </Link>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section data-testid="landing-hero" className="max-w-5xl mx-auto px-5 pt-14 pb-10 sm:pt-20 sm:pb-14 text-center">
        <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/25 rounded-full px-4 py-1.5 mb-8">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-primary font-body text-xs font-medium">Registrate y gana Bisne Cash al instante</span>
        </div>

        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.1] max-w-3xl mx-auto">
          Compra, vende y <span className="text-primary">gana</span> en Bisne
        </h1>

        <p className="mt-6 text-zinc-400 font-body text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
          El marketplace local sin comisiones. Publica gratis, negocia directo y quedate con el 100% de tu venta. Ademas, gana Bisne Cash desde el momento en que te registras.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link to="/login">
            <Button data-testid="landing-hero-cta" className="rounded-full font-body font-bold text-base bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/25 h-14 px-10">
              Crear cuenta gratis <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <Link to="/login">
            <Button data-testid="landing-hero-login" variant="outline" className="rounded-full font-body font-semibold text-sm border-zinc-700 text-zinc-300 hover:border-primary hover:text-primary h-14 px-8">
              Ya tengo cuenta
            </Button>
          </Link>
        </div>
        <p className="text-zinc-600 font-body text-xs mt-3">Gratis para siempre. Sin tarjeta. En 10 segundos.</p>
      </section>

      {/* ── Gamification Banners ── */}
      <section data-testid="landing-gamification" className="max-w-5xl mx-auto px-5 pb-16 sm:pb-20">
        <div className="grid md:grid-cols-2 gap-4">
          {/* Vault Card */}
          <div data-testid="landing-vault-card" className="relative bg-zinc-900 rounded-2xl p-6 sm:p-8 overflow-hidden border border-primary/20 shadow-lg shadow-primary/5">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-heading text-lg text-foreground">Boveda de Bienvenida</p>
                  <p className="text-primary font-body text-xs font-semibold">Regalo garantizado</p>
                </div>
              </div>
              <p className="text-zinc-300 font-body text-sm leading-relaxed">
                Registrate hoy y abre la boveda. Gana desde <span className="text-primary font-semibold">$10 hasta $100 Bisne Cash</span> al instante para usar en tu primera compra.
              </p>
              <div className="flex items-center gap-3 mt-5">
                <div className="flex -space-x-1">
                  {['$10', '$20', '$50', '$100'].map(v => (
                    <span key={v} className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-zinc-800 border-2 border-zinc-900 text-primary font-heading text-[10px]">{v}</span>
                  ))}
                </div>
                <span className="text-zinc-500 font-body text-xs">Premios posibles</span>
              </div>
            </div>
          </div>

          {/* Referral Card */}
          <div data-testid="landing-referral-card" className="relative bg-zinc-900 rounded-2xl p-6 sm:p-8 overflow-hidden border border-emerald-500/20 shadow-lg shadow-emerald-500/5">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                  <Users className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <p className="font-heading text-lg text-foreground">Invita y Gana</p>
                  <p className="text-emerald-400 font-body text-xs font-semibold">Sin limites</p>
                </div>
              </div>
              <p className="text-zinc-300 font-body text-sm leading-relaxed">
                Te invitaron? Usa el codigo de tu amigo al registrarte y ambos empezaran con <span className="text-emerald-400 font-semibold">$20 Bisne Cash extra</span>. Invita a todos los que quieras.
              </p>
              <div className="flex items-center gap-2 mt-5 bg-zinc-800/60 rounded-xl px-4 py-2.5">
                <Wallet className="w-4 h-4 text-emerald-400 shrink-0" />
                <p className="text-zinc-400 font-body text-xs">Tu amigo se registra → <span className="text-emerald-400 font-semibold">+$20</span> para ti y <span className="text-emerald-400 font-semibold">+$20</span> para el</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Benefits ── */}
      <section data-testid="landing-benefits" className="bg-zinc-900/50 border-y border-zinc-800/40">
        <div className="max-w-5xl mx-auto px-5 py-16 sm:py-20">
          <h2 className="font-heading text-3xl sm:text-4xl text-foreground text-center mb-12">
            Por que <span className="text-primary">Bisne</span>?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <div key={i} data-testid={`landing-benefit-${i}`} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 hover:border-zinc-700 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                  <b.icon className="w-6 h-6 text-primary" />
                </div>
                <p className="text-primary font-body text-xs font-semibold tracking-wider uppercase mb-2">{b.pre}</p>
                <h3 className="font-heading text-xl text-foreground leading-snug mb-3">{b.title}</h3>
                <p className="text-zinc-400 font-body text-sm leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Social Proof ── */}
      <section data-testid="landing-testimonials" className="max-w-5xl mx-auto px-5 py-16 sm:py-20">
        <h2 className="font-heading text-3xl sm:text-4xl text-foreground text-center mb-12">
          La raza ya esta haciendo <span className="text-primary">bisne</span>.
        </h2>
        <div className="grid md:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} data-testid={`landing-testimonial-${i}`} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 flex flex-col">
              <div className="flex gap-0.5 mb-4">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-primary text-primary" />)}
              </div>
              <p className="text-zinc-300 font-body text-sm leading-relaxed flex-1">"{t.text}"</p>
              <div className="mt-5 pt-4 border-t border-zinc-800">
                <p className="font-body font-semibold text-sm text-foreground">{t.author}</p>
                <p className="text-zinc-500 font-body text-xs flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{t.loc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section data-testid="landing-faq" className="bg-zinc-900/50 border-y border-zinc-800/40">
        <div className="max-w-2xl mx-auto px-5 py-16 sm:py-20">
          <h2 className="font-heading text-3xl sm:text-4xl text-foreground text-center mb-10">Preguntas frecuentes</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`landing-faq-${i}`}
                className="bg-zinc-900 border border-zinc-800 rounded-2xl px-5 overflow-hidden data-[state=open]:border-zinc-700">
                <AccordionTrigger className="font-body font-semibold text-sm text-foreground hover:no-underline py-4">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-400 font-body text-sm pb-4 leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ── Footer CTA ── */}
      <section data-testid="landing-footer-cta" className="max-w-5xl mx-auto px-5 py-16 sm:py-20">
        <div className="bg-zinc-900 border border-primary/20 rounded-3xl p-8 sm:p-12 text-center shadow-xl shadow-primary/5">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/25 rounded-full px-4 py-1.5 mb-5">
            <Gift className="w-4 h-4 text-primary" />
            <span className="text-primary font-body text-xs font-medium">Boveda + Referidos = Bisne Cash gratis</span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl text-primary mb-4">Ya se armo!</h2>
          <p className="text-zinc-400 font-body text-sm sm:text-base max-w-md mx-auto mb-8">
            Registrate, abre tu boveda, invita a tus amigos y empieza a acumular Bisne Cash.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/login">
              <Button data-testid="landing-footer-cta-btn" className="rounded-full font-body font-bold text-base bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/25 h-14 px-10">
                Crear cuenta gratis <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="rounded-full font-body font-semibold text-sm border-zinc-700 text-zinc-300 hover:border-primary hover:text-primary h-14 px-8">
                Iniciar sesion
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-zinc-800/50 py-6">
        <div className="max-w-5xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-zinc-600 font-body text-xs">Bisne 2026</span>
          <div className="flex items-center gap-4">
            <Link to="/terminos" data-testid="landing-link-terms" className="text-zinc-500 font-body text-xs hover:text-primary transition-colors">Terminos y Condiciones</Link>
            <span className="text-zinc-700">|</span>
            <Link to="/privacidad" data-testid="landing-link-privacy" className="text-zinc-500 font-body text-xs hover:text-primary transition-colors">Aviso de Privacidad</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
